/**
 * v4 Tax tips feed - refined card layout, less emoji, written for any small
 * business owner (not just shopkeepers).
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { useV4 } from './i18n';
import { listTips } from '@/services/api/client-auth';

interface LegacyTip { tag: string; titleEn: string; bodyEn: string; titleFa?: string; bodyFa?: string }

const FALLBACK_TIPS: LegacyTip[] = [
  {
    tag: 'Receipts',
    titleEn: 'Keep every receipt over $82.50',
    bodyEn: 'The ATO requires receipts for any business expense over $82.50 including GST. Snap a photo and send it to your receipts inbox - it gets filed automatically.',
    titleFa: 'هر رسید بالای ۸۲.۵۰ دلار را نگه دار',
    bodyFa: 'اداره مالیات برای هر هزینه‌ی بالای ۸۲.۵۰ دلار (با GST) رسید می‌خواهد. عکس بگیر و به ایمیل رسیدها بفرست تا اتوماتیک ثبت شود.',
  },
  {
    tag: 'Vehicle',
    titleEn: 'Claim car expenses two ways',
    bodyEn: 'Use the cents-per-km method (up to 5,000 km, $0.88/km in 2025-26) or the logbook method. Logbook gives more back if you drive a lot for work.',
    titleFa: 'هزینه‌ی ماشین را به دو روش ثبت کن',
    bodyFa: 'روش کیلومتری (حداکثر ۵۰۰۰ کیلومتر، ۰.۸۸ دلار هر کیلومتر) یا دفتر روزانه. اگر زیاد رانندگی کاری می‌کنی، دفتر روزانه بهتر است.',
  },
  {
    tag: 'Home office',
    titleEn: 'Working from home? Claim 70¢ per hour',
    bodyEn: 'The ATO\'s fixed rate covers electricity, internet, phone and stationery. Keep a record of the hours you actually worked from home.',
    titleFa: 'از خانه کار می‌کنی؟ هر ساعت ۷۰ سنت',
    bodyFa: 'نرخ ثابت اداره مالیات شامل برق، اینترنت، تلفن و لوازم اداری است. فقط ساعات کاری از خانه را یادداشت کن.',
  },
  {
    tag: 'Super',
    titleEn: 'Top up super before 30 June',
    bodyEn: 'Extra super contributions are taxed at 15% in the fund instead of your normal rate. You can put up to $30,000 a year in tax-free.',
    titleFa: 'قبل از ۳۰ ژوئن به سوپر اضافه بریز',
    bodyFa: 'پول اضافه‌ای که توی سوپر می‌ریزی فقط ۱۵٪ مالیات می‌خورد، نه نرخ معمولی شما. تا ۳۰،۰۰۰ دلار در سال.',
  },
  {
    tag: 'Asset write-off',
    titleEn: 'Instant write-off: assets under $20,000',
    bodyEn: 'If you buy equipment under $20,000 (van, computer, fridge, tools), you can deduct the whole amount this year instead of depreciating it.',
    titleFa: 'حذف فوری دارایی: زیر ۲۰،۰۰۰ دلار',
    bodyFa: 'اگر تجهیزات زیر ۲۰،۰۰۰ دلار بخری (وانت، کامپیوتر، یخچال، ابزار)، می‌توانی همه‌اش را همین امسال کسر کنی.',
  },
  {
    tag: 'Prepayments',
    titleEn: 'Pay suppliers before 30 June',
    bodyEn: 'Prepaid expenses before end of financial year can reduce your tax for this year, even if the service or goods come in the next year.',
    titleFa: 'قبل از ۳۰ ژوئن پول تامین‌کنندگان را بده',
    bodyFa: 'هزینه‌های پیش‌پرداخت قبل از پایان سال مالی مالیات امسال را کم می‌کند، حتی اگر کالا یا خدمات بعداً برسد.',
  },
  {
    tag: 'Insurance',
    titleEn: 'Business insurance is fully deductible',
    bodyEn: 'Public liability, professional indemnity, product liability - all 100% claimable. Cover your business and reduce tax at the same time.',
    titleFa: 'بیمه کسب‌وکار کاملاً قابل کسر است',
    bodyFa: 'مسئولیت عمومی، بیمه حرفه‌ای، بیمه محصول - همه ۱۰۰٪ قابل کسر است. هم خودت را پوشش بده هم مالیات را کم کن.',
  },
  {
    tag: 'Family',
    titleEn: 'Pay your spouse a fair wage',
    bodyEn: 'If your spouse helps in the business, you can pay them a normal wage. Their lower tax rate means less total tax for the household.',
    titleFa: 'به همسرت دستمزد منصفانه بده',
    bodyFa: 'اگر همسرت در کسب‌وکار کمک می‌کند، می‌توانی به او دستمزد عادی بدهی. مالیات کمتر در سطح خانواده.',
  },
];

export default function TipsPageV4() {
  const { t, settings, dir } = useV4();
  const lang = settings.lang || 'en';
  const { data: serverTips, isLoading } = useQuery({
    queryKey: ['client-tips'],
    queryFn: listTips,
    staleTime: 5 * 60_000,
  });

  // Prefer server tips; fall back to bundled static list if API fails or empty
  const tips = (serverTips && serverTips.length > 0)
    ? serverTips.map(t => ({ tag: t.tag || '', titleEn: t.titleEn, bodyEn: t.bodyEn, titleFa: t.titleFa || undefined, bodyFa: t.bodyFa || undefined }))
    : FALLBACK_TIPS;

  return (
    <div className="space-y-3 pb-4">
      <div className="flex items-center gap-2 pt-1">
        <Link
          to="/client"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40"
          aria-label="Back"
        >
          <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} />
        </Link>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('tips_title')}</p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      <div className="space-y-2">
        {tips.map((tip, i) => {
          const title = lang === 'fa' ? (tip.titleFa || tip.titleEn) : tip.titleEn;
          const body  = lang === 'fa' ? (tip.bodyFa  || tip.bodyEn)  : tip.bodyEn;
          return (
            <div key={i} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--v4-accent)] text-amber-300">
                  <Sparkles className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1">
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{tip.tag}</span>
                  <h3 className="mt-1.5 text-sm font-bold text-foreground leading-snug">{title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{body}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
